import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { AlertTriangle, ShieldCheck, FlaskConical, Users, Heart, Atom, GitCompare, TrendingDown, TrendingUp } from "lucide-react";

const getToken = () =>
  localStorage.getItem("nutramilo_access_token") ||
  sessionStorage.getItem("nutramilo_access_token");

const EVIDENCE_BADGE = {
  A: { bg: "bg-emerald-500", text: "text-white", label: "A" },
  B: { bg: "bg-sky-500", text: "text-white", label: "B" },
  C: { bg: "bg-amber-500", text: "text-white", label: "C" },
};

const PROB_COLOR = (p) => {
  if (p >= 0.7) return { color: "#ef4444", label_bg: "Висок", label_en: "High" };
  if (p >= 0.5) return { color: "#f97316", label_bg: "Умерен", label_en: "Moderate" };
  if (p >= 0.3) return { color: "#f59e0b", label_bg: "Нисък-умерен", label_en: "Low-moderate" };
  return { color: "#10b981", label_bg: "Нисък", label_en: "Low" };
};

export default function EpigeneticMapInsights({ map }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [comparison, setComparison] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    // Try to fetch comparison automatically
    const fetchCompare = async () => {
      try {
        const token = getToken();
        const { data } = await axios.get(`${API}/epigenetic-map/compare`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (data?.available) setComparison(data);
      } catch {
        /* silent — first map = no comparison */
      }
    };
    fetchCompare();
  }, [API]);

  if (!map) return null;

  const cardCls = `rounded-2xl border p-4 ${
    isDark ? "bg-[hsl(223,47%,10%)] border-white/5" : "bg-white border-gray-200"
  }`;

  return (
    <div className="space-y-4" data-testid="epigenetic-map-insights">
      {/* Lifestyle band summary */}
      <div className={cardCls}>
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-4 h-4 text-violet-500" />
          <h3 className="text-sm font-bold">{bg ? "Lifestyle епигенетичен товар" : "Lifestyle epigenetic load"}</h3>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-3xl font-bold" style={{ color: map.lifestyle_score.band === "low" ? "#10b981" : map.lifestyle_score.band === "moderate" ? "#f59e0b" : map.lifestyle_score.band === "high" ? "#f97316" : "#ef4444" }}>
            {map.lifestyle_score.composite_score}
          </div>
          <div>
            <div className="text-sm font-semibold capitalize">
              {bg ? ({ low: "нисък", moderate: "умерен", high: "висок", very_high: "много висок" }[map.lifestyle_score.band]) : map.lifestyle_score.band}
            </div>
            {map.lifestyle_score.top_drivers.length > 0 && (
              <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                {bg ? "Топ драйвери: " : "Top drivers: "}
                {map.lifestyle_score.top_drivers.join(", ")}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Family + lab gaps row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className={cardCls}>
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-bold">{bg ? "Фамилен сигнал" : "Family signal"}</h3>
          </div>
          {map.family_score.high_risk_diseases.length === 0 && map.family_score.moderate_risk_diseases.length === 0 ? (
            <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? "Няма значим фамилен сигнал." : "No significant family signal."}
            </p>
          ) : (
            <div className="space-y-1">
              {map.family_score.high_risk_diseases.length > 0 && (
                <div>
                  <span className="text-[10px] uppercase font-bold text-rose-500">{bg ? "Висок: " : "High: "}</span>
                  <span className="text-xs">{map.family_score.high_risk_diseases.join(", ")}</span>
                </div>
              )}
              {map.family_score.moderate_risk_diseases.length > 0 && (
                <div>
                  <span className="text-[10px] uppercase font-bold text-amber-500">{bg ? "Умерен: " : "Moderate: "}</span>
                  <span className="text-xs">{map.family_score.moderate_risk_diseases.join(", ")}</span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className={cardCls}>
          <div className="flex items-center gap-2 mb-2">
            <FlaskConical className="w-4 h-4 text-cyan-500" />
            <h3 className="text-sm font-bold">{bg ? "Лабораторни аномалии" : "Lab anomalies"}</h3>
          </div>
          {map.lab_gaps.length === 0 ? (
            <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? "Няма установени аномалии." : "No anomalies detected."}
            </p>
          ) : (
            <ul className="space-y-1">
              {map.lab_gaps.slice(0, 5).map((g) => (
                <li key={g.marker} className="text-xs flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                  {bg ? g.label_bg : g.label_en}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Hidden risks */}
      <div className={cardCls}>
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-pink-500" />
          <h3 className="text-sm font-bold">
            {bg ? `Топ скрити рискове (${map.hidden_risks.length})` : `Top hidden risks (${map.hidden_risks.length})`}
          </h3>
        </div>
        {map.hidden_risks.length === 0 ? (
          <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {bg ? "Не са открити скрити рискове на този етап." : "No hidden risks detected at this stage."}
          </p>
        ) : (
          <div className="space-y-2">
            {map.hidden_risks.map((r) => {
              const colorInfo = PROB_COLOR(r.probability);
              return (
                <div
                  key={r.code}
                  className={`p-2.5 rounded-xl border ${
                    isDark ? "bg-[hsl(222,47%,8%)] border-white/5" : "bg-gray-50 border-gray-200"
                  }`}
                  data-testid={`epimap-hidden-${r.code}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold">{bg ? r.name_bg : r.name_en}</div>
                      <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                        {bg ? "Чрез: " : "Through: "}
                        {r.linked_through.join(", ")}
                        {" · "}
                        {r.shared_pathways.length} {bg ? "общи пътя" : "shared pathways"}
                        {r.evidence_pmids?.length > 0 && (
                          <>
                            {" · PMIDs: "}
                            {r.evidence_pmids.map((pmid) => (
                              <a
                                key={pmid}
                                href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-violet-500 hover:underline"
                              >
                                {pmid}
                              </a>
                            )).reduce((a, b) => [a, ", ", b])}
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-base font-bold" style={{ color: colorInfo.color }}>
                        {Math.round(r.probability * 100)}%
                      </span>
                      <span className="text-[9px] uppercase" style={{ color: colorInfo.color }}>
                        {bg ? colorInfo.label_bg : colorInfo.label_en}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Prevention */}
      <div className={cardCls}>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          <h3 className="text-sm font-bold">{bg ? "Превенция (приоритизирана)" : "Prevention (prioritized)"}</h3>
        </div>
        {map.prevention.length === 0 ? (
          <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {bg ? "Няма препоръки за момента." : "No recommendations available."}
          </p>
        ) : (
          <div className="space-y-1.5">
            {map.prevention.map((p, idx) => {
              const ev = EVIDENCE_BADGE[p.evidence_level] || EVIDENCE_BADGE.C;
              return (
                <div
                  key={idx}
                  className="flex items-start gap-2"
                  data-testid={`epimap-prevention-${idx}`}
                >
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full ${ev.bg} ${ev.text} text-[10px] font-bold flex items-center justify-center`}>
                    {ev.label}
                  </span>
                  <div className="flex-1 text-xs">
                    <div className="font-medium">{bg ? p.action_bg : p.action_en}</div>
                    <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                      {bg ? "За: " : "For: "} {p.addresses.join(", ")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* LLM narrative */}
      {map.narrative && (
        <div className={`${cardCls} bg-gradient-to-br from-violet-50/50 to-pink-50/50 dark:from-violet-500/10 dark:to-pink-500/10`}>
          <h3 className="text-sm font-bold mb-2">{bg ? "AI обяснение" : "AI explanation"}</h3>
          <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isDark ? "text-gray-200" : "text-gray-800"}`}>
            {map.narrative}
          </p>
        </div>
      )}

      {/* NEW v2: Epigenetic markers (CpG sites) */}
      {map.epigenetic_markers && map.epigenetic_markers.length > 0 && (
        <div className={cardCls}>
          <div className="flex items-center gap-2 mb-3">
            <Atom className="w-4 h-4 text-fuchsia-500" />
            <h3 className="text-sm font-bold">
              {bg ? "Епигенетични маркери (CpG места)" : "Epigenetic markers (CpG sites)"}
            </h3>
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-fuchsia-100 dark:bg-fuchsia-500/20 text-fuchsia-700 dark:text-fuchsia-300">
              v2
            </span>
          </div>
          <p className={`text-[11px] mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg
              ? "Активни DNA-метилни маркери, базирани на твоя lifestyle. Това са документирани в литературата CpG места."
              : "Active DNA methylation markers based on your lifestyle. Documented CpG sites from the literature."}
          </p>
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {map.epigenetic_markers.slice(0, 10).map((cpg, idx) => (
              <div
                key={`${cpg.cpg}-${idx}`}
                className={`p-2 rounded-lg ${
                  isDark ? "bg-[hsl(222,47%,8%)] border border-white/5" : "bg-gray-50 border border-gray-200"
                }`}
                data-testid={`epimap-cpg-${cpg.cpg}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs">
                      <code className="font-mono font-bold text-fuchsia-600 dark:text-fuchsia-400">{cpg.cpg}</code>
                      <span className="text-gray-400">·</span>
                      <span className="font-semibold">{cpg.gene}</span>
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${
                        cpg.direction === "hypermethylation"
                          ? "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300"
                          : "bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300"
                      }`}>
                        {cpg.direction === "hypermethylation" ? (bg ? "Хипер" : "Hyper") : (bg ? "Хипо" : "Hypo")}
                      </span>
                    </div>
                    <p className={`text-[11px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>{cpg.effect}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[9px]">
                      <span className="text-gray-400">{bg ? "Фактор:" : "Factor:"}</span>
                      <span className="font-medium capitalize">{cpg.factor}</span>
                      <a
                        href={`https://pubmed.ncbi.nlm.nih.gov/${cpg.pmid}/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-violet-500 hover:underline"
                      >
                        PMID:{cpg.pmid}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NEW v2: Comparison with previous map */}
      {comparison?.available && (
        <div className={cardCls}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <GitCompare className="w-4 h-4 text-indigo-500" />
              <h3 className="text-sm font-bold">{bg ? "Сравнение с предишна карта" : "Comparison with previous map"}</h3>
            </div>
            <button
              onClick={() => setShowComparison((s) => !s)}
              className="text-[10px] underline text-indigo-500 hover:text-indigo-700"
              data-testid="epimap-comparison-toggle"
            >
              {showComparison ? (bg ? "Скрий" : "Hide") : (bg ? "Покажи" : "Show")}
            </button>
          </div>
          {showComparison && (
            <div className="space-y-2 text-xs">
              <div className={`flex items-center gap-2 p-2 rounded-lg ${comparison.lifestyle_improved ? "bg-emerald-50 dark:bg-emerald-500/10" : "bg-rose-50 dark:bg-rose-500/10"}`}>
                {comparison.lifestyle_improved ? (
                  <TrendingDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <TrendingUp className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                )}
                <div>
                  <strong>{bg ? "Lifestyle промяна: " : "Lifestyle change: "}</strong>
                  {comparison.lifestyle_delta > 0 ? "+" : ""}
                  {comparison.lifestyle_delta} ({comparison.previous_band} → {comparison.current_band})
                </div>
              </div>
              {comparison.new_hidden_risks?.length > 0 && (
                <div className="text-rose-600 dark:text-rose-400">
                  <strong>{bg ? "Нови рискове: " : "New risks: "}</strong>
                  {comparison.new_hidden_risks.join(", ")}
                </div>
              )}
              {comparison.resolved_hidden_risks?.length > 0 && (
                <div className="text-emerald-600 dark:text-emerald-400">
                  <strong>{bg ? "Премахнати: " : "Resolved: "}</strong>
                  {comparison.resolved_hidden_risks.join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* NEW v3: Live GWAS catalog hits for current/hidden risks */}
      {map.kb_gwas && map.kb_gwas.length > 0 && (
        <div className={cardCls}>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-base">📚</span>
            <h3 className="text-sm font-bold">{bg ? "GWAS Catalog hits (live)" : "GWAS Catalog hits (live)"}</h3>
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300">
              v3 · 192K
            </span>
          </div>
          <p className={`text-[11px] mb-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg
              ? "Genome-wide significant SNP-trait асоциации (p≤5×10⁻⁸) от EBI GWAS Catalog за заболяванията в твоята карта."
              : "Genome-wide significant SNP-trait associations (p≤5×10⁻⁸) from EBI GWAS Catalog for diseases in your map."}
          </p>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {map.kb_gwas.slice(0, 12).map((r, i) => (
              <div
                key={`${r.snp}-${i}`}
                className={`p-2 rounded-lg flex items-center justify-between gap-2 ${
                  isDark ? "bg-[hsl(222,47%,8%)] border border-white/5" : "bg-gray-50 border border-gray-200"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <a
                      href={`https://www.ncbi.nlm.nih.gov/snp/${r.snp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono font-bold text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      {r.snp}
                    </a>
                    <span className="text-gray-400">·</span>
                    <span className="font-semibold">{r.gene || "—"}</span>
                  </div>
                  <p className={`text-[10px] truncate ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {r.trait}
                  </p>
                </div>
                <span className="text-[10px] font-mono text-gray-400 flex-shrink-0">p={r.p_value < 1e-50 ? "<1e-50" : r.p_value.toExponential(1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* NEW v2.1: Earned badges (just unlocked in this calculation) */}
      {map.earned_badges && map.earned_badges.length > 0 && (
        <div className={`${cardCls} bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-500/10 dark:to-yellow-500/10 border-amber-200 dark:border-amber-500/30`} data-testid="epimap-earned-badges">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2">
            <span className="text-lg">🏆</span>
            {bg ? "Нови постижения!" : "New achievements!"}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {map.earned_badges.map((b) => (
              <div
                key={b.code}
                className={`p-2.5 rounded-lg flex items-start gap-2 ${
                  isDark ? "bg-amber-500/20" : "bg-white border border-amber-200"
                }`}
                data-testid={`epimap-badge-${b.code}`}
              >
                <span className="text-2xl flex-shrink-0">{b.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold">{bg ? b.name_bg : b.name_en}</div>
                  <div className={`text-[10px] ${isDark ? "text-amber-300" : "text-amber-800"}`}>
                    {bg ? b.description_bg : b.description_en}
                  </div>
                  <span className={`inline-block mt-1 text-[8px] uppercase font-bold tracking-wider px-1 py-0.5 rounded ${
                    b.tier === "legendary" ? "bg-purple-500 text-white"
                      : b.tier === "gold" ? "bg-yellow-500 text-white"
                      : b.tier === "silver" ? "bg-gray-400 text-white"
                      : "bg-orange-400 text-white"
                  }`}>
                    {b.tier}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className={`rounded-xl p-3 text-[11px] leading-relaxed ${
        isDark ? "bg-rose-950/20 text-rose-300 border border-rose-900/40" : "bg-rose-50 text-rose-800 border border-rose-100"
      }`}>
        <strong>{bg ? "⚠️ Дисклеймър: " : "⚠️ Disclaimer: "}</strong>
        {bg ? map.disclaimer_bg : map.disclaimer_en}
      </div>
    </div>
  );
}
